/**
 * The online lobby: create or join a room, claim seats, add AI, start. [P3-rooms] client half
 *
 * Self-contained DOM overlay so the working local-play lobby stays untouched. The seat list is the
 * important part of the UI: a device may claim SEVERAL seats, and the list has to make that obvious,
 * because that is how two people sharing this laptop join a game someone else is playing from a phone.
 */

import { MAX_SEATS, MIN_SEATS, SEAT_COLOURS, type RoomView } from '@carc/protocol';
import { RemoteSession } from './remote-session.js';

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

export class OnlineLobby {
  readonly element: HTMLElement;
  private readonly body: HTMLElement;
  private readonly status: HTMLElement;
  private session: RemoteSession | null = null;
  private started = false;
  private playerName = 'Player';

  constructor(
    private readonly onStart: (session: RemoteSession) => void,
    private readonly onCancel: () => void,
  ) {
    this.element = el('div', 'online-lobby');
    const card = el('div', 'ol-card');
    this.body = el('div', 'ol-body');
    this.status = el('div', 'ol-status');
    card.append(this.body, this.status);
    this.element.append(card);
    this.showEntry();
  }

  show(): void {
    this.element.classList.remove('hidden');
  }

  hide(): void {
    this.element.classList.add('hidden');
  }

  private setStatus(text: string, isError = false): void {
    this.status.textContent = text;
    this.status.classList.toggle('error', isError);
  }

  // ── screen 1: create or join ───────────────────────────────────────────────────────────────────

  private showEntry(): void {
    this.body.innerHTML = '';
    this.body.append(el('h2', undefined, 'Play online'));

    const nameInput = el('input', 'ol-input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Your name';
    nameInput.maxLength = 20;
    nameInput.value = localStorage.getItem('carc.name') ?? '';
    this.body.append(labelled('Name', nameInput));

    // --- create ---
    const seatSelect = el('select', 'ol-input');
    for (let n = MIN_SEATS; n <= MAX_SEATS; n++) {
      const opt = el('option', undefined, `${n} player${n === 1 ? '' : 's'}`);
      opt.value = String(n);
      if (n === 3) opt.selected = true;
      seatSelect.append(opt);
    }
    // The two room options that were hardcoded here. The server has always implemented the turn timer
    // ([P6-timer]) and the engine has always dealt across N draw stacks — but this screen sent
    // `turnSeconds: 0, stackCount: 1` every single time, so neither could ever be switched on by
    // anybody. The lobby was quietly the reason the timer "didn't work". ([D-39])
    const timerSelect = el('select', 'ol-input');
    for (const [label, value] of [
      ['Off', 0],
      ['1 minute', 60],
      ['2 minutes', 120],
      ['5 minutes', 300],
    ] as const) {
      const opt = el('option', undefined, label);
      opt.value = String(value);
      timerSelect.append(opt);
    }

    const stackSelect = el('select', 'ol-input');
    for (let n = 1; n <= 6; n++) {
      const opt = el('option', undefined, n === 1 ? 'One stack' : `${n} stacks`);
      opt.value = String(n);
      stackSelect.append(opt);
    }

    const createBtn = el('button', 'ol-btn primary', 'Create a room');
    createBtn.onclick = () => {
      const name = nameInput.value.trim() || 'Player';
      this.playerName = name;
      localStorage.setItem('carc.name', name);
      void this.begin((s) =>
        s.createRoom(name, Number(seatSelect.value), {
          stackCount: Number(stackSelect.value),
          turnSeconds: Number(timerSelect.value),
        }),
      );
    };

    // --- join ---
    const codeInput = el('input', 'ol-input code');
    codeInput.type = 'text';
    codeInput.placeholder = 'CODE';
    codeInput.maxLength = 4;
    codeInput.oninput = () => {
      codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    };
    const joinBtn = el('button', 'ol-btn', 'Join');
    joinBtn.onclick = () => {
      const name = nameInput.value.trim() || 'Player';
      const code = codeInput.value.trim();
      if (code.length !== 4) return this.setStatus('A room code is 4 characters.', true);
      this.playerName = name;
      localStorage.setItem('carc.name', name);
      void this.begin((s) => s.joinRoom(code, name));
    };

    const createRow = el('div', 'ol-row');
    createRow.append(
      labelled('Seats', seatSelect),
      labelled('Turn timer', timerSelect),
      labelled('Draw stacks', stackSelect),
      createBtn,
    );
    const joinRow = el('div', 'ol-row');
    joinRow.append(labelled('Room code', codeInput), joinBtn);

    const back = el('button', 'ol-btn ghost', 'Back');
    back.onclick = () => this.onCancel();

    this.body.append(createRow, el('div', 'ol-sep', 'or'), joinRow, back);

    // A shared link (?room=CODE) drops you straight into the join flow.
    const fromUrl = new URLSearchParams(location.search).get('room');
    if (fromUrl) {
      codeInput.value = fromUrl.toUpperCase().slice(0, 4);
      this.setStatus(`Room ${codeInput.value} — enter your name and join.`);
    }
  }

  private async begin(action: (s: RemoteSession) => void): Promise<void> {
    this.setStatus('Connecting…');
    const session = new RemoteSession({
      onRoom: (room) => this.showSeats(room),
      onError: (msg) => this.setStatus(msg, true),
      onStarted: () => {
        if (this.started) return;
        this.started = true;
        this.hide();
        this.onStart(session);
      },
      onConnectionChange: (connected) => {
        if (!connected && !this.started) this.setStatus('Connection lost — reconnecting…', true);
      },
    });
    this.session = session;
    try {
      await session.connect();
      action(session);
    } catch {
      this.setStatus('Could not reach the game server. Is it running?', true);
    }
  }

  // ── screen 2: the seat table ───────────────────────────────────────────────────────────────────

  private showSeats(room: RoomView): void {
    if (this.started) return;
    const session = this.session;
    if (!session) return;

    this.body.innerHTML = '';
    const head = el('div', 'ol-head');
    head.append(el('h2', undefined, 'Room'), el('span', 'ol-code', room.code));

    const copy = el('button', 'ol-btn ghost', 'Copy invite link');
    copy.onclick = () => {
      const url = `${location.origin}${location.pathname}?room=${room.code}`;
      void navigator.clipboard?.writeText(url);
      copy.textContent = 'Copied!';
      setTimeout(() => (copy.textContent = 'Copy invite link'), 1500);
    };
    head.append(copy);
    this.body.append(head);

    const mine = room.seats.filter((s) => s.mine).length;
    this.body.append(
      el(
        'p',
        'ol-hint',
        mine > 1
          ? `You are holding ${mine} seats on this device — you'll be prompted to pass it along between turns.`
          : 'Claim more than one seat if several of you are sharing this device.',
      ),
    );

    const list = el('div', 'ol-seats');
    for (const seat of room.seats) {
      const row = el('div', `ol-seat${seat.mine ? ' mine' : ''}`);
      const swatch = el('span', 'ol-swatch');
      swatch.style.background = SEAT_COLOURS.find((c) => c.id === seat.colour)?.hex ?? '#888';

      const who = seat.controller === 'ai'
        ? `Computer (${seat.aiDifficulty ?? 'steady'})`
        : seat.claimed
          ? `${seat.name}${seat.mine ? ' — you' : ''}${seat.connected ? '' : ' (away)'}`
          : 'Empty';
      row.append(swatch, el('span', 'ol-seat-name', who));

      const actions = el('div', 'ol-seat-actions');
      if (seat.mine) {
        const release = el('button', 'ol-btn tiny', 'Release');
        release.onclick = () => session.releaseSeat(seat.index);
        actions.append(release);
      } else if (!seat.claimed && seat.controller !== 'ai') {
        const claim = el('button', 'ol-btn tiny primary', 'Sit here');
        // Carry the name, or everyone shows up as "Seat 2" on the scoreboard.
        const label = mine === 0 ? this.playerName : `${this.playerName} (${mine + 1})`;
        claim.onclick = () => session.claimSeat(seat.index, label);
        actions.append(claim);
      }
      if (!seat.claimed) {
        const ai = el('button', 'ol-btn tiny', seat.controller === 'ai' ? 'Remove AI' : 'Add AI');
        ai.onclick = () => session.setSeatAi(seat.index, seat.controller !== 'ai');
        actions.append(ai);
      }
      row.append(actions);
      list.append(row);
    }
    this.body.append(list);

    // The server requires EVERY seat filled before it will start, so mirror that here rather than
    // letting the player press a button that is going to be refused.
    const empty = room.seats.filter((s) => !s.claimed && s.controller !== 'ai');
    const humans = room.seats.filter((s) => s.claimed).length;

    if (empty.length > 0) {
      const fill = el('button', 'ol-btn', `Fill ${empty.length === 1 ? 'the last seat' : `all ${empty.length} empty seats`} with AI`);
      fill.onclick = () => {
        for (const seat of empty) session.setSeatAi(seat.index, true);
      };
      this.body.append(fill);
    }

    const start = el('button', 'ol-btn primary', 'Start the game');
    start.disabled = empty.length > 0 || humans === 0;
    start.onclick = () => session.startGame();
    this.body.append(start);

    this.setStatus(
      empty.length > 0
        ? `${empty.length} seat${empty.length === 1 ? '' : 's'} still empty — wait for people to join, or fill them with AI.`
        : humans === 0
          ? 'Sit down somewhere before starting.'
          : 'Ready when you are.',
    );
  }
}

function labelled(text: string, input: HTMLElement): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'ol-field';
  wrap.append(document.createTextNode(text), input);
  return wrap;
}
